import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import * as XLSX from "xlsx";

interface ExcelPayload {
  inputs?: Record<string, unknown>;
  outputs?: Record<string, unknown>;
  warnings?: string[] | unknown[];
  technical?: Record<string, unknown>;
}

export async function generateExcel(
  payload: ExcelPayload = {},
  filename: string = "drive_in_results.xlsx",
): Promise<void> {
  try {
    const wb = XLSX.utils.book_new();

    const sheetData: any[][] = [];

    const addSection = (
      title: string,
      data: Record<string, unknown> | unknown[],
    ): void => {
      sheetData.push([`--- ${title} ---`, ""]);

      if (Array.isArray(data)) {
        if (data.length === 0) {
          sheetData.push(["None", ""]);
        } else {
          data.forEach((item, index) => {
            sheetData.push([`[${index + 1}]`, String(item)]);
          });
        }
      } else if (typeof data === "object" && data !== null) {
        const keys = Object.keys(data);
        if (keys.length === 0) {
          sheetData.push(["None", ""]);
        } else {
          for (const key of keys) {
            const value = data[key];

            if (Array.isArray(value)) {
              sheetData.push([
                `${key} (Array Sample)`,
                `[${value.length} items]`,
              ]);
              const limit = Math.min(5, value.length);
              for (let i = 0; i < limit; i++) {
                sheetData.push([`${key}[${i}]`, String(value[i])]);
              }
            } else if (typeof value === "object" && value !== null) {
              sheetData.push([key, JSON.stringify(value)]);
            } else {
              sheetData.push([key, String(value)]);
            }
          }
        }
      }
      sheetData.push([]);
    };

    addSection("INPUTS", payload.inputs || {});
    addSection("OUTPUTS", payload.outputs || {});
    addSection("WARNINGS", payload.warnings || []);
    addSection("TECHNICAL DETAILS", payload.technical || {});

    const wsSummary = XLSX.utils.aoa_to_sheet(sheetData);
    wsSummary["!cols"] = [{ wch: 30 }, { wch: 25 }];
    XLSX.utils.book_append_sheet(wb, wsSummary, "Results Summary");

    if (Capacitor.isNativePlatform()) {
      const wbout = XLSX.write(wb, { bookType: "xlsx", type: "base64" });

      const uniqueFileName = `${Date.now()}_${filename}`;
      const savedFile = await Filesystem.writeFile({
        path: uniqueFileName,
        data: wbout,
        directory: Directory.Cache,
      });

      await Share.share({
        title: "Export Excel",
        text: "Sharing your results",
        url: savedFile.uri,
        dialogTitle: "Share Excel File",
      });
    } else {
      XLSX.writeFile(wb, filename);
    }
  } catch (err) {
    throw err;
  }
}
